import type { AvailableFacetForMagicFill, FormFieldsMetadata } from "@/shared/types/magicFillTypes";

/**
 * Helper para criar metadata de formulário para o Magic Fill
 * Centraliza a configuração de tipos, opções e limites dos campos
 */

// Organization Form Metadata (usado no onboarding e admin)
export const getOrganizationFormMetadata = (): FormFieldsMetadata => ({
  name: {
    type: "text",
    maxLength: 100,
    required: true,
  },
  info: {
    type: "textarea",
    maxLength: 500,
    required: true,
  },
  industry: {
    type: "text",
    maxLength: 200,
    required: true,
  },
  size: {
    type: "select",
    options: ["1-10", "11-50", "51-200", "201-500", "501-1000", "1001-5000", "5000+"],
    required: true,
  },
  stage: {
    type: "select",
    options: ["idea", "startup", "growth", "established", "enterprise"],
    required: true,
  },
  website: {
    type: "url",
    maxLength: 255,
    required: true,
  },
});

// Product Form Metadata
export const getWorkspaceFormMetadata = (): FormFieldsMetadata => ({
  name: {
    type: "text",
    maxLength: 100,
    required: true,
  },
  description: {
    type: "textarea",
    maxLength: 500,
    required: true,
  },
  website: {
    type: "url",
    maxLength: 255,
    required: false,
  },
  repositoryUrl: {
    type: "url",
    maxLength: 255,
    required: false,
  },
});

// Knowledge Import Form Metadata
// PBI 79319 — aceita `availableFacets` para o campo `facets` (vocabulário
// controlado da organização). Quando ausente, o campo facets não é incluído
// no metadata (a IA não tenta preencher).
export const getKnowledgeImportFormMetadata = (
  availableFacets?: AvailableFacetForMagicFill[]
): FormFieldsMetadata => {
  const base: FormFieldsMetadata = {
    title: {
      type: "text",
      maxLength: 200,
      required: true,
    },
    description: {
      type: "textarea",
      maxLength: 1000,
      required: false,
    },
    url: {
      type: "url",
      maxLength: 500,
      required: false,
    },
    tags: {
      type: "text",
      maxLength: 200,
      required: false,
    },
    // PBI 79328 — atributos de negócio do documento. Magic Fill os preenche a
    // partir do conteúdo para que o documento fique categorizável e filtrável.
    "metadata.source": {
      type: "text",
      maxLength: 80,
      required: false,
    },
    "metadata.subject": {
      type: "text",
      maxLength: 120,
      required: false,
    },
    "metadata.language": {
      type: "text",
      maxLength: 20,
      required: false,
    },
    "metadata.tags": {
      type: "text",
      maxLength: 300,
      required: false,
    },
  };
  if (availableFacets && availableFacets.length > 0) {
    base.facets = {
      type: "facets",
      required: false,
      availableFacets,
    };
  }
  return base;
};

// Skill Register Form Metadata (AB#85634)
// Mesmo arranjo do import da KB: o campo `facets` só entra quando há vocabulário
// da organização para oferecer. Sem `availableFacets`, o serviço não injeta as
// instruções de faceta e a sugestão seria descartada na normalização — então é
// melhor não pedir.
export const getSkillRegisterFormMetadata = (
  availableFacets?: AvailableFacetForMagicFill[]
): FormFieldsMetadata => {
  const base: FormFieldsMetadata = {
    title: {
      type: "text",
      maxLength: 120,
      required: true,
    },
    description: {
      type: "textarea",
      maxLength: 1024,
      required: true,
    },
    // As ferramentas que o pacote cita, em lista curta. O limite de tamanho é o
    // que segura a quantidade: sem ele, o modelo devolvia uma frase inteira e
    // cada palavra dela virava etiqueta — "Python (FastMCP) and Node/TypeScript
    // for MCP server development" saiu como dez `tool:` na tela.
    tools: {
      type: "text",
      maxLength: 120,
      required: false,
    },
  };

  if (availableFacets && availableFacets.length > 0) {
    base.facets = {
      type: "facets",
      required: false,
      availableFacets,
    };
  }

  return base;
};

/**
 * Helper genérico para criar metadata de campo individual
 */
export const createFieldMetadata = {
  text: (maxLength?: number, required = false) => ({
    type: "text" as const,
    maxLength,
    required,
  }),
  textarea: (maxLength?: number, required = false) => ({
    type: "textarea" as const,
    maxLength,
    required,
  }),
  email: (maxLength?: number, required = false) => ({
    type: "email" as const,
    maxLength,
    required,
  }),
  url: (maxLength?: number, required = false) => ({
    type: "url" as const,
    maxLength,
    required,
  }),
  number: (min?: number, max?: number, required = false) => ({
    type: "number" as const,
    min,
    max,
    required,
  }),
  select: (options: string[], required = false) => ({
    type: "select" as const,
    options,
    required,
  }),
  radio: (options: string[], required = false) => ({
    type: "radio" as const,
    options,
    required,
  }),
  checkbox: (required = false) => ({
    type: "checkbox" as const,
    required,
  }),
};
